import { useEffect, useState } from 'react';
import axios from 'axios';
import { DEFAULT_LIVE_WINDOW_MINUTES, DEFAULT_REFRESH_INTERVAL_MS, EMPTY_ENTRIES } from './machineStatDataConstants';

export default function useEntryData({ dataset, points, rangeMode, from, to }) {
  const { identifier, areaField = 'area_identifier', fields = EMPTY_ENTRIES } = dataset?.entryData ?? {};
  const area = dataset?.key;
  const last = points[points.length - 1];
  const start = points[0]?.date ?? (rangeMode === 'range' ? from : undefined);
  const end = last ? new Date(Date.parse(last.date) + (last.durationMinutes ?? 1) * 60_000).toISOString() : rangeMode === 'range' ? to : undefined;
  const scope = `${identifier}|${areaField}|${area}|${rangeMode}|${start}|${end}|${fields.map((field) => field.key).join(',')}`;
  const [data, setData] = useState({ entries: EMPTY_ENTRIES, loading: false, error: '' });

  useEffect(() => {
    if (!identifier || (rangeMode !== 'live' && (!start || !end))) return undefined;
    let cancelled = false;
    const fetchEntryData = async () => {
      setData((current) => ({ ...current, scope, entries: current.scope === scope ? current.entries : EMPTY_ENTRIES, loading: current.scope !== scope, error: '' }));
      const params = {
        identifier,
        from_: start ?? new Date(Date.now() - DEFAULT_LIVE_WINDOW_MINUTES * 60_000).toISOString(),
        until: end ?? new Date().toISOString()
      };
      try {
        const response = await axios.get('/api/v1/productionline/flowStatistic/flat/', { params });
        const entries = [];
        response.data
          .filter((entry) => entry[areaField] === area)
          .sort((left, right) => Date.parse(left.added_at) - Date.parse(right.added_at) || left.id - right.id)
          .forEach((entry) => {
            const values = Object.fromEntries(fields.map((field) => [field.key, entry[field.key]]));
            const previous = entries[entries.length - 1];
            if (!previous || fields.some((field) => previous.data[field.key] !== values[field.key])) {
              const start = Date.parse(entry.added_at);
              if (previous) previous.end = start;
              entries.push({ timestamp: entry.added_at, data: values, start, end: Infinity });
            }
          });
        if (!cancelled) setData({ scope, entries, loading: false, error: '' });
      } catch (error) {
        if (!cancelled) setData({ scope, entries: EMPTY_ENTRIES, loading: false, error: error.response?.data?.error ?? 'Could not load production data.' });
      }
    };
    fetchEntryData();
    const timer = rangeMode === 'live' ? window.setInterval(fetchEntryData, DEFAULT_REFRESH_INTERVAL_MS) : undefined;
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [identifier, areaField, area, fields, rangeMode, scope, start, end]);

  return { entries: data.scope === scope ? data.entries : EMPTY_ENTRIES, loading: Boolean(identifier) && (data.scope !== scope || data.loading), error: data.scope === scope ? data.error : '' };
}
